/**
 * Admin Statistics Page
 * 
 * Dashboard for administrators showing platform-wide statistics and user engagement metrics.
 * Provides overview of users, clients, assessments, and posture analyses.
 */


import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/shared/PageLayout';
import { useToast } from '@/hooks/use-toast';
import { BarChart3, Users, FileText, PersonStanding, TrendingUp, Calendar, Activity, Clock, Download, Check, Loader2, Bot, Sparkles, MessageSquare, Zap } from 'lucide-react';
import { UserRole } from '@/components/authnz/types/rbac';
import { useAdminAccessGate } from '@/contexts/use-admin-access-gate';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { apiFetch } from '@/lib/api-client';
import logger, { getCorrelationId } from '@/lib/logging';

interface TopActiveUser {
    userId: string;
    email: string;
    displayName: string;
    roles?: UserRole[];
    primaryRole?: string | null;
    accessStatus?: string | null;
    lastLoginAt: string | null;
    clientCount: number;
    assessmentCount?: number;
    postureAnalysisCount?: number;
    activityScore: number;
    subscriptionStatus?: string | null;
}

const AVAILABLE_ROLES: { role: UserRole; label: string }[] = [
    { role: UserRole.PRACTITIONER, label: 'Practitioners' },
    { role: UserRole.STUDENT, label: 'Students' },
    { role: UserRole.TUTOR, label: 'Tutors' },
    { role: UserRole.CLIENT, label: 'Clients' },
    { role: UserRole.SALES, label: 'Sales' },
];

interface PlatformStatistics {
    totalUsers: number;
    activeUsersThisWeek: number;
    activeUsersThisMonth?: number;
    newUsersThisWeek: number;
    newUsersThisMonth: number;
    totalPractitioners?: number;
    activePractitionersThisMonth?: number;
    totalClients: number;
    totalAssessments: number;
    totalPostureAnalyses: number;
    clientsAddedLastWeek: number;
    assessmentsAddedLastWeek: number;
    postureAnalysesAddedLastWeek: number;
    clientsAddedLastMonth?: number;
    assessmentsAddedLastMonth?: number;
    postureAnalysesAddedLastMonth?: number;
    totalChatConversations?: number;
    chatConversationsThisMonth?: number;
    totalChatMessages?: number;
    chatMessagesThisMonth?: number;
    totalHarryNotes?: number;
    harryNotesThisMonth?: number;
    avgClientsPerUser: number;
    avgAssessmentsPerClient: number;
    avgPostureAnalysesPerClient: number;
    avgAssessmentsPerPractitionerMonthly?: number;
    avgPostureAnalysesPerPractitionerMonthly?: number;
    avgChatPerPractitionerMonthly?: number;
    topActiveUsers: TopActiveUser[];
    generatedAt: string;
}

export function AdminStatisticsPage() {
    const { user: currentUser, serverUser, loading } = useAuth();
    const navigate = useNavigate();
    const { toast } = useToast();
    const {
        isReady,
        isAuthorizedAdmin,
        isAdminModeEnabled,
    } = useAdminAccessGate();
    const [hasAccess, setHasAccess] = useState(false);
    const [isChecking, setIsChecking] = useState(true);
    const [statistics, setStatistics] = useState<PlatformStatistics | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Interactive filters & export state
    const [selectedRange, setSelectedRange] = useState<'30d' | '60d' | 'all'>('30d');
    const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([UserRole.PRACTITIONER]);
    const [activeUsers, setActiveUsers] = useState<TopActiveUser[]>([]);
    const [isUsersLoading, setIsUsersLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [a11yStatus, setA11yStatus] = useState<string>('');

    useEffect(() => {
        if (!isReady) {
            return;
        }

        if (!currentUser || !serverUser) {
            navigate('/login');
            setHasAccess(false);
            setIsChecking(false);
            return;
        }

        const isAdmin = serverUser.roles?.includes(UserRole.ADMINISTRATOR);
        if (!isAdmin) {
            toast({
                title: 'Access Denied',
                description: 'You must be an administrator to access this page.',
                variant: 'destructive',
            });
            navigate('/');
            setHasAccess(false);
        } else if (!isAdminModeEnabled || !isAuthorizedAdmin) {
            toast({
                title: 'Admin Mode Required',
                description: 'Please enable Admin Mode in Settings to access this page.',
                variant: 'destructive',
            });
            navigate('/settings');
            setHasAccess(false);
        } else {
            setHasAccess(true);
        }

        setIsChecking(false);
    }, [
        currentUser,
        serverUser,
        isReady,
        isAdminModeEnabled,
        isAuthorizedAdmin,
        navigate,
        toast,
    ]);

    // Fetch top-level platform statistics when access is granted
    useEffect(() => {
        const fetchStatistics = async () => {
            if (!hasAccess) return;

            setIsLoading(true);
            setError(null);

            const fetchStarted = performance.now();
            const correlationId = getCorrelationId();
            try {
                const data = await apiFetch<PlatformStatistics>('/admin/statistics');
                setStatistics(data);
                logger.debug('[AdminStatistics] platform statistics fetch', {
                    category: 'adminStatistics',
                    event: 'adminStatistics.fetch',
                    durationMs: Math.round(performance.now() - fetchStarted),
                    correlationId,
                    ok: true,
                });
            } catch (err: any) {
                logger.error('[AdminStatistics] platform statistics fetch failed', {
                    category: 'adminStatistics',
                    event: 'adminStatistics.fetch',
                    durationMs: Math.round(performance.now() - fetchStarted),
                    correlationId,
                    ok: false,
                    error: err,
                });
                setError(err.message || 'Failed to load statistics');
                toast({
                    title: 'Error',
                    description: 'Failed to load platform statistics.',
                    variant: 'destructive',
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchStatistics();
    }, [hasAccess, toast]);

    // Fetch filtered active users when filters change
    useEffect(() => {
        if (!hasAccess) return;

        let isMounted = true;
        const fetchActiveUsers = async () => {
            setIsUsersLoading(true);
            try {
                const rolesParam = selectedRoles.length > 0 ? selectedRoles.join(',') : UserRole.PRACTITIONER;
                const data = await apiFetch<{ users: TopActiveUser[]; count: number }>(
                    `/admin/statistics/users?range=${selectedRange}&roles=${rolesParam}&limit=25`
                );
                if (isMounted) {
                    setActiveUsers(data.users || []);
                    setA11yStatus(`Showing ${data.users?.length || 0} active users for ${selectedRange === '30d' ? 'last 30 days' : selectedRange === '60d' ? 'last 60 days' : 'all time'}.`);
                }
            } catch (err) {
                logger.warn('[AdminStatistics] Failed to fetch filtered active users', { err });
            } finally {
                if (isMounted) {
                    setIsUsersLoading(false);
                }
            }
        };

        fetchActiveUsers();

        return () => {
            isMounted = false;
        };
    }, [hasAccess, selectedRange, selectedRoles]);

    const toggleRole = (role: UserRole) => {
        setSelectedRoles(prev => {
            if (prev.includes(role)) {
                const next = prev.filter(r => r !== role);
                return next.length > 0 ? next : [role]; // Keep at least one selected
            } else {
                return [...prev, role];
            }
        });
    };

    const handleExportCsv = async () => {
        if (isExporting) return;
        setIsExporting(true);
        const rangeLabel = selectedRange === '30d' ? '30 days' : selectedRange === '60d' ? '60 days' : 'all time';
        setA11yStatus(`Generating CSV export for users active in ${rangeLabel}, please wait...`);

        try {
            const rolesParam = selectedRoles.length > 0 ? selectedRoles.join(',') : UserRole.PRACTITIONER;
            const response = await fetch(
                `/api/protected/admin/statistics/export-users?range=${selectedRange}&roles=${rolesParam}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'X-Correlation-Id': getCorrelationId(),
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Export failed with HTTP ${response.status}`);
            }

            const blob = await response.blob();
            const disposition = response.headers.get('content-disposition');
            let filename = `harry-users-${selectedRange}-${new Date().toISOString().split('T')[0]}.csv`;
            if (disposition && disposition.includes('filename=')) {
                const match = disposition.match(/filename="?([^"]+)"?/);
                if (match?.[1]) {
                    filename = match[1];
                }
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            setA11yStatus(`CSV export download ready: ${filename}`);
            toast({
                title: 'Export Complete',
                description: `Successfully downloaded ${filename}`,
            });
        } catch (err: any) {
            logger.error('[AdminStatistics] CSV export error', { error: err });
            setA11yStatus('CSV export failed.');
            toast({
                title: 'Export Failed',
                description: err?.message || 'Could not download CSV export.',
                variant: 'destructive',
            });
        } finally {
            setIsExporting(false);
        }
    };

    if (isChecking || !isReady) {
        return (
            <PageLayout title="Platform Statistics" isLoading={true}>
                <div className="flex items-center justify-center h-full">
                    <p className="text-muted-foreground">Verifying access...</p>
                </div>
            </PageLayout>
        );
    }

    if (!hasAccess) {
        return null;
    }

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const StatCard = ({
        title,
        value,
        subtitle,
        icon: Icon,
        trend,
        loading
    }: {
        title: string;
        value: number | string;
        subtitle?: string;
        icon: any;
        trend?: { value: number; label: string };
        loading?: boolean;
    }) => (
        <Card className="rounded-xl border border-border bg-card card-padding">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</CardTitle>
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <Skeleton className="h-8 w-20 rounded-md" />
                ) : (
                    <>
                        <div className="text-3xl font-extrabold font-mono tracking-tight text-foreground tabular-nums">{value}</div>
                        {subtitle && <p className="text-xs text-muted-foreground mt-1 font-medium leading-relaxed">{subtitle}</p>}
                        {trend && (
                            <div className="flex items-center text-[10px] mt-2 bg-success-surface/80 text-success-text border border-success/20 px-2.5 py-0.5 rounded-full w-fit font-semibold">
                                <TrendingUp className="h-2.5 w-2.5 mr-1" />
                                <span>+{trend.value}</span>
                                <span className="opacity-80 ml-1">{trend.label}</span>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );

    const getRankBadge = (rank: number) => {
        if (rank === 1) return <span className="flex items-center justify-center w-6 h-6 rounded-full bg-warning text-warning-foreground/20 text-warning-text font-bold text-xs shadow-sm border border-warning/30">1</span>;
        if (rank === 2) return <span className="flex items-center justify-center w-6 h-6 rounded-full bg-muted/20 text-foreground dark:text-muted-foreground font-bold text-xs shadow-sm border border-border/30">2</span>;
        if (rank === 3) return <span className="flex items-center justify-center w-6 h-6 rounded-full bg-warning/20 text-warning-text font-bold text-xs shadow-sm border border-warning/30">3</span>;
        return <span className="flex items-center justify-center w-6 h-6 font-medium text-xs text-muted-foreground">{rank}</span>;
    };

    const displayedUsers = activeUsers.length > 0 ? activeUsers : (statistics?.topActiveUsers || []);
    const showLoadingUsers = isUsersLoading || (isLoading && activeUsers.length === 0);

    return (
        <PageLayout
            title="Platform Statistics"
            subtitle="Overview of user engagement and platform activity"
        >
            {/* Screen Reader Live Status Region */}
            <div className="sr-only" role="status" aria-live="polite">
                {a11yStatus}
            </div>

            <div className="space-y-6">
                {statistics && (
                    <p className="text-xs text-muted-foreground text-right -mt-4">
                        Last updated: {formatDate(statistics.generatedAt)}
                    </p>
                )}

                {error && (
                    <Card className="mb-6 border-destructive bg-error-surface/10">
                        <CardContent className="pt-6">
                            <p className="text-destructive font-medium">{error}</p>
                        </CardContent>
                    </Card>
                )}

                {/* Overview Stats */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                            <Activity className="h-3.5 w-3.5 text-primary" />
                            Platform Overview
                        </h2>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <StatCard
                            title="Total Users"
                            value={statistics?.totalUsers ?? 0}
                            subtitle={`${statistics?.newUsersThisMonth ?? 0} new in last 30 days`}
                            icon={Users}
                            trend={statistics ? { value: statistics.newUsersThisMonth, label: 'this mo' } : undefined}
                            loading={isLoading}
                        />
                        <StatCard
                            title="Active Users"
                            value={statistics?.activeUsersThisMonth ?? statistics?.activeUsersThisWeek ?? 0}
                            subtitle={`${statistics?.activeUsersThisWeek ?? 0} active in last 7 days`}
                            icon={Activity}
                            trend={statistics ? { value: statistics.activeUsersThisWeek, label: 'this week' } : undefined}
                            loading={isLoading}
                        />
                        <StatCard
                            title="Total Clients"
                            value={statistics?.totalClients ?? 0}
                            subtitle={`Avg ${statistics?.avgClientsPerUser ?? 0} per user`}
                            icon={Users}
                            trend={statistics ? { value: statistics.clientsAddedLastMonth ?? statistics.clientsAddedLastWeek, label: 'this mo' } : undefined}
                            loading={isLoading}
                        />
                        <StatCard
                            title="Total SOP Assessments"
                            value={statistics?.totalAssessments ?? 0}
                            subtitle={`Avg ${statistics?.avgAssessmentsPerClient ?? 0} per client`}
                            icon={FileText}
                            trend={statistics ? { value: statistics.assessmentsAddedLastMonth ?? statistics.assessmentsAddedLastWeek, label: 'this mo' } : undefined}
                            loading={isLoading}
                        />
                    </div>
                </div>

                {/* Practitioner Monthly Velocity & Clinical Activity */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                            <Zap className="h-3.5 w-3.5 text-primary" />
                            Practitioner Monthly Velocity
                        </h2>
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                        <StatCard
                            title="SOP Analyses / Month"
                            value={statistics ? `+${statistics.assessmentsAddedLastMonth ?? 0}` : 0}
                            subtitle={`Avg ${statistics?.avgAssessmentsPerPractitionerMonthly ?? 0} per practitioner / mo`}
                            icon={FileText}
                            trend={statistics ? { value: statistics.assessmentsAddedLastWeek, label: 'this week' } : undefined}
                            loading={isLoading}
                        />
                        <StatCard
                            title="Posture Analyses / Month"
                            value={statistics ? `+${statistics.postureAnalysesAddedLastMonth ?? 0}` : 0}
                            subtitle={`Avg ${statistics?.avgPostureAnalysesPerPractitionerMonthly ?? 0} per practitioner / mo`}
                            icon={PersonStanding}
                            trend={statistics ? { value: statistics.postureAnalysesAddedLastWeek, label: 'this week' } : undefined}
                            loading={isLoading}
                        />
                        <StatCard
                            title="Active Practitioners"
                            value={statistics?.activePractitionersThisMonth ?? 0}
                            subtitle={`of ${statistics?.totalPractitioners ?? 0} total practitioners active (30d)`}
                            icon={Users}
                            trend={statistics && statistics.totalPractitioners ? { value: Math.round(((statistics.activePractitionersThisMonth ?? 0) / Math.max(statistics.totalPractitioners, 1)) * 100), label: '% active' } : undefined}
                            loading={isLoading}
                        />
                    </div>
                </div>

                {/* AI & Chat Assistant Engagement */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                            <Bot className="h-3.5 w-3.5 text-primary" />
                            AI & Chat Assistant Usage
                        </h2>
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                        <StatCard
                            title="AI Conversations"
                            value={statistics?.totalChatConversations ?? 0}
                            subtitle={`Avg ${statistics?.avgChatPerPractitionerMonthly ?? 0} / practitioner / mo`}
                            icon={Bot}
                            trend={statistics ? { value: statistics.chatConversationsThisMonth ?? 0, label: 'this mo' } : undefined}
                            loading={isLoading}
                        />
                        <StatCard
                            title="Chat Messages Exchanged"
                            value={statistics?.totalChatMessages ?? 0}
                            subtitle={`+${statistics?.chatMessagesThisMonth ?? 0} messages in last 30 days`}
                            icon={MessageSquare}
                            trend={statistics ? { value: statistics.chatMessagesThisMonth ?? 0, label: 'this mo' } : undefined}
                            loading={isLoading}
                        />
                        <StatCard
                            title="AI Clinical Notes (Harry Notes)"
                            value={statistics?.totalHarryNotes ?? 0}
                            subtitle="AI notes generated for clients"
                            icon={Sparkles}
                            trend={statistics ? { value: statistics.harryNotesThisMonth ?? 0, label: 'this mo' } : undefined}
                            loading={isLoading}
                        />
                    </div>
                </div>

                {/* Platform Users & Export Card */}
                <Card className="rounded-xl border border-border overflow-hidden bg-card">
                    <CardHeader className="bg-muted/30 border-b border-border/80 pb-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <CardTitle className="text-lg font-bold tracking-tight text-foreground">Active Users & Activity Export</CardTitle>
                                <CardDescription className="text-sm text-muted-foreground mt-1">
                                    Filter and download regular users active in the selected window
                                </CardDescription>
                            </div>
                            <Button
                                type="button"
                                variant="default"
                                size="sm"
                                onClick={handleExportCsv}
                                disabled={isExporting}
                                aria-label={`Download CSV spreadsheet of ${selectedRoles.join(', ')} active in ${selectedRange === '30d' ? 'the last 30 days' : selectedRange === '60d' ? 'the last 60 days' : 'all time'}`}
                                className="h-9 px-4 gap-2 font-semibold shadow-sm w-full md:w-auto"
                            >
                                {isExporting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        <span>Generating CSV...</span>
                                    </>
                                ) : (
                                    <>
                                        <Download className="h-4 w-4" />
                                        <span>Export CSV</span>
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Filter Toolbar: Time Window + Role Multi-Select */}
                        <div className="mt-4 pt-3 border-t border-border/40 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                            {/* Time Window Tabs */}
                            <div className="flex flex-wrap items-center gap-1.5 p-1 rounded-lg bg-muted/50 border border-border/60" role="radiogroup" aria-label="Filter active users by date range">
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={selectedRange === '30d'}
                                    onClick={() => setSelectedRange('30d')}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                                        selectedRange === '30d'
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                                    }`}
                                >
                                    Last 30 Days
                                </button>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={selectedRange === '60d'}
                                    onClick={() => setSelectedRange('60d')}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                                        selectedRange === '60d'
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                                    }`}
                                >
                                    Last 60 Days (~2 Months)
                                </button>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={selectedRange === 'all'}
                                    onClick={() => setSelectedRange('all')}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                                        selectedRange === 'all'
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                                    }`}
                                >
                                    All Time
                                </button>
                            </div>

                            {/* Role Multi-Select Chips */}
                            <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-xs font-medium text-muted-foreground mr-1">Roles:</span>
                                {AVAILABLE_ROLES.map(({ role, label }) => {
                                    const isSelected = selectedRoles.includes(role);
                                    return (
                                        <button
                                            key={role}
                                            type="button"
                                            onClick={() => toggleRole(role)}
                                            aria-pressed={isSelected}
                                            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border transition-all ${
                                                isSelected
                                                    ? 'bg-primary/10 border-primary/40 text-primary font-semibold'
                                                    : 'bg-muted/30 border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/50'
                                            }`}
                                        >
                                            {isSelected && <Check className="w-3 h-3 text-primary" />}
                                            <span>{label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                        {showLoadingUsers ? (
                            <div className="space-y-2">
                                {[...Array(5)].map((_, i) => (
                                    <Skeleton key={i} className="h-12 w-full rounded-lg" />
                                ))}
                            </div>
                        ) : displayedUsers && displayedUsers.length > 0 ? (
                            <div className="overflow-x-auto rounded-lg border border-border/60">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="hover:bg-transparent border-b border-border/60">
                                            <TableHead className="w-16">Rank</TableHead>
                                            <TableHead>User</TableHead>
                                            <TableHead className="text-center">Clients</TableHead>
                                            <TableHead className="text-center">Last Login</TableHead>
                                            <TableHead className="text-right">Score</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {displayedUsers.map((user, index) => (
                                            <TableRow key={user.userId} className="hover:bg-muted/30 transition-colors duration-150 h-12 border-b border-border/60 last:border-b-0">
                                                <TableCell className="font-medium py-3">
                                                    {getRankBadge(index + 1)}
                                                </TableCell>
                                                <TableCell className="py-3">
                                                    <div className="flex flex-col">
                                                        <span className="font-semibold text-foreground">{user.displayName}</span>
                                                        <span className="text-xs text-muted-foreground">{user.email}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-center py-3">
                                                    <Badge variant="secondary" className="bg-primary/10 text-primary border-none px-2.5 py-0.5 rounded-full font-medium">
                                                        {user.clientCount}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-center text-sm py-3 text-muted-foreground font-mono">
                                                    {formatDate(user.lastLoginAt)}
                                                </TableCell>
                                                <TableCell className="text-right py-3">
                                                    <Badge variant="default" className="bg-primary hover:bg-primary/95 text-primary-foreground px-2.5 py-0.5 rounded-full font-bold">
                                                        {user.activityScore}
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <p className="text-muted-foreground text-center py-8">No users found matching the selected filters.</p>
                        )}
                    </CardContent>
                </Card>
            </div>
        </PageLayout>
    );
}
